/**
 * Remote Debug Logging Service
 * Enables remote console monitoring from PC when debugging on iOS devices.
 *
 * Activation: URL param ?kassie-debug=voorsmakie2026
 * Persists via sessionStorage for the PWA session lifetime.
 */

const MAGIC_PARAM = 'kassie-debug';
const MAGIC_VALUE = 'voorsmakie2026';
const SESSION_KEY = 'kassie-debug-session';
const MAX_BUFFER = 200;
const MAX_OVERLAY_ENTRIES = 50;
const FLUSH_INTERVAL_MS = 2000;

/**
 * Determine API base URL (same logic as api.js)
 */
function getDebugApiBaseUrl() {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'http://localhost:8000';
    }
    if (hostname.startsWith('192.168.') || hostname.startsWith('10.')) {
        return 'http://localhost:8000';
    }
    return 'https://api.kassie.media';
}

const API_ENDPOINT = getDebugApiBaseUrl() + '/api/debug/logs';

let sessionId = null;
const buffer = [];
let flushTimer = null;
const overlayEntries = [];
let overlayEl = null;
let panelExpanded = false;

/**
 * Generate a short 6-character hex session ID
 * @returns {string}
 */
function generateSessionId() {
    const arr = new Uint8Array(3);
    crypto.getRandomValues(arr);
    return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Check if debug mode should be active
 * @returns {boolean}
 */
function isDebugActive() {
    // Check URL param: ?kassie-debug=voorsmakie2026
    const params = new URLSearchParams(window.location.search);
    if (params.get(MAGIC_PARAM) === MAGIC_VALUE) {
        return true;
    }
    // Check hash-based activation: #kassie-debug
    if (window.location.hash === '#kassie-debug') {
        return true;
    }
    // Check sessionStorage (persists after initial activation)
    return !!sessionStorage.getItem(SESSION_KEY);
}

/**
 * Flush buffered log entries to backend
 */
async function flushLogs() {
    if (buffer.length === 0) return;

    const entries = buffer.splice(0, buffer.length);

    try {
        await fetch(API_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId, entries }),
        });
    } catch {
        // Re-queue entries on failure (prepend, respecting max)
        buffer.unshift(...entries);
        if (buffer.length > MAX_BUFFER) {
            buffer.splice(0, buffer.length - MAX_BUFFER);
        }
    }
}

/**
 * Add a log entry to the buffer and overlay
 * @param {string} level
 * @param {string} message
 */
function addEntry(level, message) {
    const entry = {
        level,
        message: String(message).slice(0, 2000),
        timestamp: new Date().toISOString(),
    };

    // In-memory buffer (FIFO)
    buffer.push(entry);
    if (buffer.length > MAX_BUFFER) {
        buffer.shift();
    }

    // Overlay entries
    overlayEntries.push(entry);
    if (overlayEntries.length > MAX_OVERLAY_ENTRIES) {
        overlayEntries.shift();
    }

    // Update overlay panel if expanded
    if (panelExpanded) {
        renderPanel();
    }

    // Flush immediately on errors
    if (level === 'error') {
        flushLogs();
    }
}

/**
 * Intercept console methods
 */
function interceptConsole() {
    const levels = ['log', 'error', 'warn', 'info'];

    levels.forEach((level) => {
        const original = console[level].bind(console);
        console[level] = (...args) => {
            original(...args);
            const message = args.map((a) => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' ');
            addEntry(level, message);
        };
    });
}

/**
 * Capture unhandled errors and rejections
 */
function captureGlobalErrors() {
    window.onerror = (message, source, lineno, colno, error) => {
        const msg = `${message} at ${source}:${lineno}:${colno}${error?.stack ? '\n' + error.stack : ''}`;
        addEntry('error', msg);
        return false;
    };

    window.addEventListener('unhandledrejection', (event) => {
        const reason = event.reason;
        const msg = reason instanceof Error ? `Unhandled rejection: ${reason.message}\n${reason.stack}` : `Unhandled rejection: ${String(reason)}`;
        addEntry('error', msg);
    });
}

/**
 * Get color for log level
 * @param {string} level
 * @returns {string}
 */
function getLevelColor(level) {
    switch (level) {
        case 'error': return '#ff6b6b';
        case 'warn': return '#ffd93d';
        case 'info': return '#6bcfff';
        default: return '#ffffff';
    }
}

/**
 * Render the log panel contents
 */
function renderPanel() {
    const panel = overlayEl?.querySelector('[data-debug-panel]');
    if (!panel) return;

    panel.innerHTML = overlayEntries
        .slice()
        .reverse()
        .map(
            (e) =>
                `<div style="color:${getLevelColor(e.level)};margin-bottom:2px;word-break:break-all;">` +
                `<span style="opacity:0.5;font-size:9px;">${e.timestamp.slice(11, 19)}</span> ` +
                `<span style="font-weight:bold;">[${e.level.toUpperCase()}]</span> ` +
                `${escapeHtml(e.message.slice(0, 300))}` +
                `</div>`
        )
        .join('');

    // Auto-scroll to top (newest first)
    panel.scrollTop = 0;
}

/**
 * Escape HTML special characters
 * @param {string} str
 * @returns {string}
 */
function escapeHtml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Create the floating overlay UI
 */
function createOverlay() {
    overlayEl = document.createElement('div');
    overlayEl.id = 'kassie-debug-overlay';
    overlayEl.setAttribute('style', [
        'position:fixed',
        'bottom:8px',
        'left:8px',
        'z-index:99999',
        'pointer-events:none',
        'font-family:monospace',
        'font-size:11px',
    ].join(';'));

    // Badge
    const badge = document.createElement('div');
    badge.setAttribute('style', [
        'pointer-events:auto',
        'background:rgba(0,0,0,0.75)',
        'color:var(--kassie-orange-4, #ff9500)',
        'padding:4px 8px',
        'border-radius:4px',
        'cursor:pointer',
        'user-select:none',
        'display:inline-block',
        'backdrop-filter:blur(4px)',
    ].join(';'));
    badge.textContent = `🐛 ${sessionId}`;
    badge.title = 'Tap to toggle debug log panel';

    badge.addEventListener('click', () => {
        panelExpanded = !panelExpanded;
        const panel = overlayEl.querySelector('[data-debug-panel]');
        if (panelExpanded) {
            panel.style.display = 'block';
            renderPanel();
        } else {
            panel.style.display = 'none';
        }
    });

    // Panel
    const panel = document.createElement('div');
    panel.setAttribute('data-debug-panel', '');
    panel.setAttribute('style', [
        'pointer-events:auto',
        'display:none',
        'background:rgba(0,0,0,0.85)',
        'color:#fff',
        'padding:6px',
        'border-radius:4px',
        'margin-top:4px',
        'max-height:200px',
        'max-width:90vw',
        'width:360px',
        'overflow-y:auto',
        'overflow-x:hidden',
        'font-size:10px',
        'line-height:1.3',
        'backdrop-filter:blur(4px)',
        '-webkit-overflow-scrolling:touch',
    ].join(';'));

    overlayEl.appendChild(badge);
    overlayEl.appendChild(panel);
    document.body.appendChild(overlayEl);
}

/**
 * Initialize the debug service.
 * No-op if magic param is not present (zero overhead in production).
 */
export function initDebugService() {
    if (!isDebugActive()) {
        // Even when not active, register the secret tap gesture
        // so it can be activated without URL manipulation
        registerSecretTapGesture();
        return;
    }

    activateDebugMode();
}

/**
 * Register keyboard shortcut to activate debug mode.
 * Ctrl+Shift+D activates it (not triggerable by children tapping).
 */
function registerSecretTapGesture() {
    const keys = new Set();

    document.addEventListener('keydown', (e) => {
        keys.add(e.key.toLowerCase());
        // Ctrl + Shift + D + B + U (all held simultaneously)
        if (e.ctrlKey && e.shiftKey && keys.has('b') && keys.has('h') && keys.has('u')) {
            keys.clear();
            activateDebugMode();
        }
    });

    document.addEventListener('keyup', (e) => {
        keys.delete(e.key.toLowerCase());
    });
}

/**
 * Activate debug mode (create session, intercept console, show overlay)
 */
function activateDebugMode() {
    if (sessionId) return; // Already active

    // Get or create session ID
    const existingSession = sessionStorage.getItem(SESSION_KEY);
    if (existingSession) {
        sessionId = existingSession;
    } else {
        sessionId = generateSessionId();
        sessionStorage.setItem(SESSION_KEY, sessionId);
    }

    // Intercept console
    interceptConsole();

    // Capture global errors
    captureGlobalErrors();

    // Start flush timer
    flushTimer = setInterval(flushLogs, FLUSH_INTERVAL_MS);

    // Create overlay once DOM is ready
    if (document.body) {
        createOverlay();
    } else {
        document.addEventListener('DOMContentLoaded', createOverlay);
    }

    // Log activation
    console.info(`[DebugService] Active — session: ${sessionId}`);
}